import React from 'react';
import PropTypes from 'prop-types';
import utils from "../utils/utils";
import {Page, Text, View, Document, StyleSheet, Font} from '@react-pdf/renderer';
import DATE_FORMAT from "../components/constants/dateFormat";
import regularFont from "../assets/fonts/Metropolis-Regular.ttf"
import boldFont from "../assets/fonts/Metropolis-SemiBold.ttf"

const MarketplaceAdminPayoutReportPdfDocument = ({payoutReport, orders, copy}) => {
  const BORDER_COLOR = '#bfbfbf'
  const BORDER_STYLE = 'solid'

  Font.register({
    family: 'metropolis',
    fonts: [
      { src: regularFont },
      { src: boldFont, fontWeight: 600 }
    ]
  });

  // disable hyphenation
  Font.registerHyphenationCallback(word => [word]);

  const styles = StyleSheet.create({
    body: {
      padding: 30,
      fontSize: 12,
      fontFamily: 'metropolis',
    },
    title: {
      fontSize: 24,
      marginTop: 50,
      marginBottom: 50,
    },
    h3: {
      fontSize: 16,
    },
    small: {
      fontSize: 10,
    },
    bold: {
      fontWeight: 700,
    },
    info: {
      marginBottom: 50,
    },
    table: {
      display: "table",
      width: "auto",
      borderStyle: BORDER_STYLE,
      borderColor: BORDER_COLOR,
      borderWidth: 1,
      borderRightWidth: 0,
      borderBottomWidth: 0
    },
    tableRow: {
      margin: "auto",
      flexDirection: "row"
    },
    tableColHeader: {
      borderStyle: BORDER_STYLE,
      borderColor: BORDER_COLOR,
      borderWidth: 1,
      borderLeftWidth: 0,
      borderTopWidth: 0
    },
    tableCol: {
      borderStyle: BORDER_STYLE,
      borderColor: BORDER_COLOR,
      borderWidth: 1,
      borderLeftWidth: 0,
      borderTopWidth: 0
    },
    tableCol1: {
      width: '12%',
    },
    tableCol2: {
      width: '20%',
    },
    tableCol3: {
      width: '30%',
    },
    tableCol4: {
      width: '19%',
    },
    tableCol5: {
      width: '19%',
    },
    tableColHalf: {
      width: "25%",
      borderStyle: BORDER_STYLE,
      borderColor: BORDER_COLOR,
      borderWidth: 1,
      borderLeftWidth: 0,
      borderTopWidth: 0
    },
    tableColThird: {
      width: "16.6%",
      borderStyle: BORDER_STYLE,
      borderColor: BORDER_COLOR,
      borderWidth: 1,
      borderLeftWidth: 0,
      borderTopWidth: 0
    },
    tableCellHeader: {
      margin: 5,
      fontWeight: 700
    },
    tableCell: {
      margin: 5,
    },
    footer: {
      position: 'absolute',
      bottom: 30,
      left: 30,
    }
  });
  
  return (
    <Document>
      <Page style={styles.body}>
        <Text style={styles.title}>
          {copy.body.header}
        </Text>

        <View style={styles.info}>
          <Text>
            <Text style={styles.bold}>{copy.info.recipientName}: </Text><Text>
              {payoutReport.recipientName}
            </Text>
          </Text>
          <Text>
            <Text style={styles.bold}>{copy.info.iban}: </Text><Text>
              {payoutReport.iban}
            </Text>
          </Text>
          <Text>
            <Text style={styles.bold}>{copy.body.dateCreated}: </Text><Text>
              {utils.getDateFromString(payoutReport.dateCreated).format(DATE_FORMAT.displayWithTime)}
            </Text>
          </Text>
          <Text>
            <Text style={styles.bold}>{copy.info.orderCount}: </Text><Text>
              {orders.length}
            </Text>
          </Text>
        </View>

        <View style={[styles.table, {marginBottom: 5}]}>
          <View style={styles.tableRow}>
            <View style={[styles.tableColHeader, styles.tableCol1]}>
              <Text style={styles.tableCellHeader}>
                {copy.table.orderId}
              </Text>
            </View>
            <View style={[styles.tableColHeader, styles.tableCol2]}>
              <Text style={styles.tableCellHeader}>
                {copy.table.name}
              </Text>
            </View>
            <View style={[styles.tableColHeader, styles.tableCol3]}>
              <Text style={styles.tableCellHeader}>
                {copy.table.items}
              </Text>
            </View>
            <View style={[styles.tableColHeader, styles.tableCol4]}>
              <Text style={styles.tableCellHeader}>
                {copy.table.totalWithoutVat}
              </Text>
            </View>
            <View style={[styles.tableColHeader, styles.tableCol5]}>
              <Text style={styles.tableCellHeader}>
                {copy.table.total}
              </Text>
            </View>
          </View>
          {
            orders.map((order, index) => (
              <View key={index} style={styles.tableRow}>
                <View style={[styles.tableCol, styles.tableCol1]}>
                  <Text style={styles.tableCell}>{order.id}</Text>
                </View>
                <View style={[styles.tableCol, styles.tableCol2]}>
                  <Text style={styles.tableCell}>{order.user.screenName} {"\n"}{utils.getDateFromString(order.dateCreated).format(DATE_FORMAT.displayWithTime)}</Text>
                </View>
                <View style={[styles.tableCol, styles.tableCol3]}>
                  {
                    order.items.map((orderItem, index) => (
                      <Text key={index} style={styles.tableCell}>{orderItem.quantity} x {orderItem.itemName}</Text>
                    ))
                  }
                </View>
                <View style={[styles.tableCol, styles.tableCol4]}>
                  {order.finalSumWithoutVat? (
                    <Text style={styles.tableCell}>{order.finalSumWithoutVat}€</Text>
                  ): (<Text></Text>)}
                </View>
                <View style={[styles.tableCol, styles.tableCol5]}>
                  <Text style={styles.tableCell}>{order.finalSum}€</Text>
                  {
                    order.refunds.map((refund, index) => (
                      <Text key={index} style={[styles.tableCell, styles.small]}>-{refund.amount}€ refund "{refund.comment}"</Text>
                    ))
                  }
                </View>
              </View>
            ))
          }
        </View>
        <Text style={[{textAlign: 'right', marginBottom: 5, marginTop: 5}]}>
          <Text>{copy.total.amountBeforeDeductionsWithoutVat}: </Text><Text style={[styles.bold]}>
          {payoutReport.amountBeforeDeductionsWithoutVat}€</Text>
        </Text>
        {
          payoutReport.vatBeforeDeductions? (
            <Text style={[{textAlign: 'right', marginBottom: 5, marginTop: 5}]}>
              <Text>{copy.total.vatBeforeDeductions}: </Text><Text style={[styles.bold]}>
              {payoutReport.vatBeforeDeductions}€</Text>
            </Text>
          ): (<Text></Text>)
        }
        <Text style={[{textAlign: 'right', marginBottom: 5, marginTop: 5}]}>
          <Text>{copy.total.amountBeforeDeductions}: </Text><Text style={[styles.bold]}>
          {payoutReport.amountBeforeDeductions}€</Text>
        </Text>
        <Text style={[{textAlign: 'right', marginBottom: 5, marginTop: 5}]}>
          <Text>{copy.total.commission}: </Text><Text style={[styles.bold]}>
          {payoutReport.commission}€</Text>
        </Text>
        <Text style={[styles.h3, {textAlign: 'right'}]}>
          <Text>{copy.total.total}: </Text><Text style={[styles.bold]}>
          {payoutReport.amount}€</Text>
        </Text>
      </Page>
    </Document>
  );
};

MarketplaceAdminPayoutReportPdfDocument.propTypes = {
  payoutReport: PropTypes.object.isRequired,
  orders: PropTypes.array.isRequired,
  copy: PropTypes.object.isRequired,
};

MarketplaceAdminPayoutReportPdfDocument.defaultProps = {};

export default MarketplaceAdminPayoutReportPdfDocument;